import { makeObservable, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import { Form, FormFeedback, FormGroup, Input, Alert, Button } from 'reactstrap';
import BusyButton from '~/components/BusyButton';
import ErrorList from '~/components/ErrorList';
import { EmailHelpText } from './EmailHelpText';
import { EmailHelpPopover } from './EmailHelpPopover';
import { getAppContext } from '~/lib/AppContext';
import { field, is, validate } from '~/lib/helper/ValidationHelper';
import FormStore from '~/lib/store/FormStore';

interface RecoverFormValues {
    email: string;
}

function emptyFormValues(): RecoverFormValues {
    return {
        email: '',
    };
}

@observer
export default class RecoverPane extends React.Component {
    recoverForm = new FormStore<RecoverFormValues>(emptyFormValues());

    @observable
    showSuccess = false;

    constructor(props: Record<string, never>) {
        super(props);
        makeObservable(this);
    }

    componentDidMount(): void {
        runInAction(() => {
            this.showSuccess = false;
        });
        this.recoverForm.init(emptyFormValues());
    }

    async doRecover() {
        if (
            !this.recoverForm.validate((e) =>
                validate(e, field('email', is.required('Email is required'), is.minLength(1, 'Email is required')))
            )
        ) {
            return;
        }

        await getAppContext().siteApiClient.sendResetToken(this.recoverForm.values.email);
        runInAction(() => {
            this.showSuccess = true;
        });
    }

    render() {
        return (
            <>
                <div className="preLoginTitle">
                    <h2>Forgot password?</h2>
                    <p>
                        Enter your email below and click the "Reset password" button. An email will be sent to the
                        provided address allowing you to reset your password.
                    </p>
                </div>

                {this.showSuccess && (
                    <Alert color="success" className="mb-4">
                        <i className="icon ion-md-checkmark-circle me-2" />
                        An email has been sent with information on how to reset your password
                    </Alert>
                )}

                <Form
                    onSubmit={(e) => {
                        e.preventDefault();
                        void this.recoverForm.doSubmit(() => this.doRecover(), {
                            errorSummary: 'Unable to send password reset email',
                        });
                    }}
                >
                    <FormGroup className="mb-4">
                        <label className="form-label" htmlFor={this.recoverForm.idFor('email')}>
                            Email
                        </label>
                        <EmailHelpPopover />
                        <Input
                            id={this.recoverForm.idFor('email')}
                            type="email"
                            autoComplete="username"
                            placeholder="Enter your email"
                            {...this.recoverForm.inputProps('email')}
                            disabled={this.showSuccess}
                        />
                        <FormFeedback>
                            <ErrorList errors={this.recoverForm.errorsFor('email')} />
                        </FormFeedback>
                    </FormGroup>

                    {!this.showSuccess && (
                        <>
                            <div className="loginActions mb-3">
                                <BusyButton
                                    type="submit"
                                    busy={this.recoverForm.isSubmitting}
                                    color="primary"
                                    size="lg"
                                    className="w-100"
                                >
                                    <i className="icon ion-md-mail me-2" />
                                    Reset password
                                </BusyButton>
                            </div>

                            <div className="text-center">
                                <a href="/login" className="text-muted">
                                    <i className="icon ion-md-arrow-back me-1" />
                                    Log in
                                </a>
                            </div>
                        </>
                    )}

                    {this.showSuccess && (
                        <>
                            <div className="loginActions mb-3">
                                <Button
                                    type="button"
                                    color="primary"
                                    size="lg"
                                    href="/login"
                                    className="w-100"
                                    autoFocus
                                >
                                    <i className="icon ion-md-arrow-back me-2" />
                                    Log in
                                </Button>
                            </div>

                            <div className="emailHelp text-muted">
                                <EmailHelpText variant="sent" />
                            </div>
                        </>
                    )}
                </Form>
            </>
        );
    }
}
